/**
 * How a parked ("sleeping") client is described to their coach (#4530).
 *
 * A coach can park a claimed client without disconnecting them. The two places
 * that show one — the client list and the client detail page — both need the
 * same sentence, so it is written here once rather than in each of them. See
 * `design-docs/coach-mode.md` → "Sleeping clients".
 */
import { DateTime } from 'luxon';
import { CoachClientSlot, classifyCoachSlotsForCap, pickSlotsToSleepForCap } from '@cf-shared/data-model/coach';
import { TranslatedString } from 'models/translated-string';
import { LL } from 'i18n/i18n.service';
import type { TranslationFunctions } from '../../gensrc/i18n/i18n-types';

/**
 * "Asleep since today", or "Asleep for 12 days".
 *
 * Takes the translation functions as a parameter, defaulting to the live ones,
 * so a test can drive it in a chosen language without a running app — the same
 * shape `formatRelativeDay` uses in `formatting.ts`.
 *
 * Since #4454 it also says WHY, because the server can park a client too — see
 * the note inside the function.
 *
 * Two things it deliberately does NOT do:
 *
 * - It does not decide WHETHER the client is asleep. Callers ask
 *   `isSlotSleeping` for that. This only describes the duration, and a slot
 *   that has never been parked has no timestamp to describe.
 * - It does not fall back to a made-up duration when the timestamp is missing
 *   or unreadable. It returns the plain "Asleep" label instead, because a
 *   confident "Asleep for 0 days" would be a worse answer than no number at
 *   all. A missing timestamp is possible: the two fields are written together,
 *   but they are separate fields on a document two devices can write.
 */
export function describeAsleepDuration(
  slot: Pick<CoachClientSlot, 'sleepStateChangedAt' | 'sleptBy'> | undefined,
  now: DateTime,
  ll: TranslationFunctions = LL,
): TranslatedString {
  // WHO parked them changes the whole sentence, not a word inside it (#4454).
  //
  // A client the coach parked themselves needs no explanation — they did it and
  // they know why. A client the SERVER parked, because the coach moved to a
  // smaller plan and went over its client limit, very much does: without this
  // the coach sees somebody asleep who they never touched, and the only
  // explanation available to them is guesswork.
  //
  // It is also what makes an undone wake understandable. A coach at their limit
  // is allowed to wake somebody, and the server then parks that same client
  // again straight away. Without a reason on the row, that reads as the app
  // ignoring them.
  const parkedByServer = slot?.sleptBy === 'auto';
  const days = daysAsleep(slot, now);

  if (parkedByServer) {
    if (days === undefined) return ll.coach.clientAsleep.becausePlanFull();
    if (days <= 0) return ll.coach.clientAsleep.becausePlanFullToday();
    return ll.coach.clientAsleep.becausePlanFullSince({ days });
  }

  if (days === undefined) return ll.coach.clientAsleep.badge();
  if (days <= 0) return ll.coach.clientAsleep.badgeSinceToday();
  return ll.coach.clientAsleep.badgeSince({ days });
}

/**
 * Whole days since the client was parked, or undefined when that cannot be
 * worked out.
 *
 * Counts calendar days elapsed rather than 24-hour blocks, so a client parked
 * yesterday evening reads as one day rather than none — which is what a person
 * looking at the list means by "how long has this been sitting there".
 *
 * A timestamp in the future yields 0, not a negative number. Clocks disagree,
 * and "asleep for -1 days" is the kind of thing that reaches a screenshot.
 */
function daysAsleep(
  slot: Pick<CoachClientSlot, 'sleepStateChangedAt'> | undefined,
  now: DateTime,
): number | undefined {
  const since = slot?.sleepStateChangedAt;
  if (!since) return undefined;
  // Read the stored moment into the SAME zone `now` is in before taking either
  // start-of-day. The timestamp is written in UTC and the reader is in their own
  // zone, so comparing one zone's calendar day against another's would shift the
  // answer by a day for anybody east or west of UTC — for several hours around
  // midnight, every day.
  const parsed = DateTime.fromISO(since).setZone(now.zone);
  if (!parsed.isValid) return undefined;
  const days = now.startOf('day').diff(parsed.startOf('day'), 'days').days;
  return Math.max(0, Math.floor(days));
}

/**
 * How many places on a coach's plan are free, as shown to the coach (#4530).
 *
 * Pure and separate from the page so it can be tested — the page's version was
 * a getter on an Angular class with no test file, and all three of the things
 * this returns are decisions rather than arithmetic:
 *
 * - `used` counts by the SAME rule the server enforces
 *   (`classifyCoachSlotsForCap`), so the app and the server cannot disagree
 *   about whether the coach has room. Parked clients and un-sent draft invites
 *   are both excluded by that rule.
 * - `atCapacity` compares the UNFLOORED used count, so a coach who is over
 *   their limit is still refused the Add Client button.
 * - `remaining` is floored at zero, because a coach genuinely can be over their
 *   limit — a plan downgrade, or waking a parked client — and "-2 slots
 *   remaining" should never reach a screen. The floor is display-only, which is
 *   why it must not be applied to `atCapacity` as well.
 */
export function computeCoachSlotAvailability(
  slots: CoachClientSlot[],
  maxSlots: number,
): { used: number; remaining: number; atCapacity: boolean } {
  const counts = classifyCoachSlotsForCap(slots);
  const used = counts.claimed + counts.outstandingInvites;
  return {
    used,
    remaining: Math.max(0, maxSlots - used),
    atCapacity: used >= maxSlots,
  };
}

/**
 * Which clients a plan with the given client limit would leave no room for,
 * and how to name them (#4454) — or null when nobody would be paused.
 *
 * Both coach-facing warnings run through this: the confirmation shown before a
 * plan change made inside our app, and the standing notice shown while an Apple
 * plan change is queued. They differ only in when they are shown and what
 * sentence wraps the answer, so the decision itself lives here once, pure and
 * testable, rather than twice inside two pages.
 *
 * **The clients come from the same function the SERVER uses to choose**,
 * `pickSlotsToSleepForCap`. That is the whole reason that rule lives in shared
 * code. Working the list out separately is how a warning ends up naming
 * different people from the ones who actually lose access.
 *
 * Returns null — meaning say nothing — when the cap is missing or unusable, as
 * well as when the coach already fits. `pickSlotsToSleepForCap` owns that
 * refusal; this passes the cap straight through to it.
 */
export function computeClientsToPause(
  slots: CoachClientSlot[],
  cap: number | undefined,
): { clients: CoachClientSlot[]; count: number; names: string } | null {
  const clients = pickSlotsToSleepForCap(slots, cap as number);
  if (clients.length === 0) return null;
  return { clients, count: clients.length, names: joinClientNamesForNotice(clients) };
}

/**
 * The client names for a "these will be paused" notice, as one string.
 *
 * The name is the nickname the coach gave the client, which is the only name a
 * slot carries. A slot with no name at all is dropped rather than left as a gap
 * in the middle of a sentence.
 */
export function joinClientNamesForNotice(slots: CoachClientSlot[]): string {
  return slots
    .map((slot) => slot.nickname)
    .filter((name) => !!name)
    .join(', ');
}
